import CheckIcon from '@heroicons/react/24/outline/CheckIcon';
import ChevronDownIcon from '@heroicons/react/24/outline/ChevronDownIcon';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/core/DropdownMenu/DropdownMenu';
import { useTranslation } from '@/hooks/useTranslation';
import { isSameSelection } from '@/pages/dam/utils/damCardView';
import { cn } from '@/services/utils';

interface DamCardViewMenuOption {
    name: string;
    label: string;
}

interface DamCardViewMenuProps {
    /** Fields of the `asset_view_options` layout, in menu order. */
    options: DamCardViewMenuOption[];
    /** Saved selection. */
    selection: string[];
    /** Selection that equals the `asset_card` layout; the trigger then says "Default". */
    defaultSelection: string[];
    onSave: (selection: string[]) => void;
}

const itemClassName = cn(
    'tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-rounded tw-px-3 tw-py-2 tw-text-t-sm tw-text-t-primary tw-outline-none lg:tw-text-t-base',
    'data-[highlighted]:tw-bg-fill-primary-hover',
);

/**
 * Menu "Card view" of the library toolbar (wireframe `6636:47353`): pick the fields shown
 * on cards and as list columns. Changes apply with "Save" (D-020); closing discards them.
 */
function DamCardViewMenu({ options, selection, defaultSelection, onSave }: DamCardViewMenuProps) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState(selection);

    const handleOpenChange = (open: boolean) => {
        if (open) {
            setDraft(selection);
        }
        setIsOpen(open);
    };

    const toggle = (name: string, checked: boolean) => {
        const next = checked ? [...draft, name] : draft.filter((entry) => entry !== name);
        setDraft(options.map((option) => option.name).filter((entry) => next.includes(entry)));
    };

    const handleSave = () => {
        onSave(draft);
        setIsOpen(false);
    };

    const viewLabel = isSameSelection(selection, defaultSelection) ? t('DAM.LIBRARY.CARD_VIEW_DEFAULT') : t('DAM.LIBRARY.CARD_VIEW_CUSTOM');

    return (
        <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" disabled={options.length === 0}>
                    <span>{t('DAM.LIBRARY.CARD_VIEW')} {viewLabel}</span>
                    <ChevronDownIcon aria-hidden className="tw-h-4 tw-w-4 tw-icon-primary" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" hasBorder className="tw-flex tw-w-64 tw-flex-col tw-gap-2">
                <DropdownMenuPrimitive.Label className="tw-px-3 tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">
                    {t('DAM.LIBRARY.CARD_VIEW_SETTINGS')}
                </DropdownMenuPrimitive.Label>
                <div className="tw-flex tw-flex-col">
                    {options.map((option) => (
                        <DropdownMenuPrimitive.CheckboxItem
                            key={option.name}
                            checked={draft.includes(option.name)}
                            onCheckedChange={(checked) => toggle(option.name, checked === true)}
                            onSelect={(event) => event.preventDefault()}
                            className={itemClassName}
                        >
                            <span className="tw-inline-flex tw-h-4 tw-w-4 tw-shrink-0">
                                <DropdownMenuPrimitive.ItemIndicator>
                                    <CheckIcon aria-hidden className="tw-h-4 tw-w-4 tw-icon-primary" />
                                </DropdownMenuPrimitive.ItemIndicator>
                            </span>
                            <span>{option.label}</span>
                        </DropdownMenuPrimitive.CheckboxItem>
                    ))}
                </div>
                <Button onClick={handleSave} disabled={isSameSelection(draft, selection)} className="tw-w-full">
                    {t('DAM.LIBRARY.CARD_VIEW_SAVE')}
                </Button>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export { DamCardViewMenu };
export type { DamCardViewMenuOption, DamCardViewMenuProps };
